import React, { useState, useEffect, useCallback, useRef } from 'react';
import { syncApi, databasesApi } from '../../services/api';
import {
  Database,
  Play,
  Loader,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  RotateCcw,
  ArrowRight,
  Layers,
  Zap,
  Copy,
  Shuffle,
  Lock,
  Ban
} from 'lucide-react';

// ── Shared schema: all 33 destination tables (same across all projects) ──
const TIERS = [
  { tier: 0, label: 'Reference', tables: [
    'Building', 'Card_Type', 'Commissioning_Key', 'Discipline', 'Eng_Units',
    'ISA_Type', 'Manufacturer', 'Organization', 'Panel_Type', 'Source',
    'Subnet', 'System_Code', 'Transfer_Party', 'Valve_Size', 'Wire_Type'
  ]},
  { tier: 1, label: 'Core', tables: ['Drawing'] },
  { tier: 2, label: 'Dependent', tables: [
    'Index_Sheet', 'SOO_Index', 'Drawing_Log', 'SOO_Log', 'SOO_Equipment'
  ]},
  { tier: 3, label: 'Structural', tables: [
    'Panels', 'IP_Address_DLN', 'IP_Address_SLN', 'Networks_ENET',
    'Networks_Serial', 'Estimate', 'PO_Lines'
  ]},
  { tier: 4, label: 'Complex', tables: [
    'Tags', 'Panel_Terminal', 'PLC_MSG_Map', 'Pwr_Dist'
  ]}
];

const TOTAL_TABLES = TIERS.reduce((sum, t) => sum + t.tables.length, 0);

// Per-project: which tables are NOT in the source (exist in dest but won't be synced)
const PROJECT_META = {
  GWA: {
    label: 'GWA',
    db: 'pmd-preprod-db-gwa01',
    excludedTables: [],  // all 33 synced
    customTransforms: [
      { table: 'Estimate', type: 'rename', desc: 'Column renames (spaces \u2192 underscores)' },
      { table: 'Networks_ENET', type: 'explicit', desc: 'Explicit column list (computed cols Line_1/2/3)' },
      { table: 'Networks_Serial', type: 'rename', desc: 'Table rename from Networks_CNET' },
      { table: 'Pwr_Dist', type: 'merge', desc: 'Dual-source merge (Pnl_Pwr_Dist + Inst_Pwr_Dist)' },
      { table: 'Tags', type: 'filter', desc: 'From Tags_Phase1, excludes Register' }
    ]
  },
  PNA: {
    label: 'PNA',
    db: 'pmd-preprod-db-pna01',
    excludedTables: ['Estimate', 'PO_Lines', 'Networks_Serial', 'PLC_MSG_Map'],
    customTransforms: [
      { table: 'System_Code', type: 'filter', desc: 'Excludes System_ID column' },
      { table: 'Tags', type: 'filter', desc: 'Excludes Register, Wire_Label_Line1/2' },
      { table: 'Pwr_Dist', type: 'merge', desc: 'Device_Tag cascading lookup (Panel \u2192 Tag \u2192 Load)' }
    ]
  },
  GFS: {
    label: 'GFS',
    db: 'pmd-preprod-db-gfs01',
    excludedTables: ['Estimate', 'PO_Lines', 'Panel_Terminal', 'Pwr_Dist'],
    customTransforms: [
      { table: 'Networks_Serial', type: 'rename', desc: 'Table rename from Networks_CNET' },
      { table: 'Tags', type: 'merge', desc: '4-phase UNION (Phase1/2/3 + UPW), auto-gen IDs' }
    ]
  }
};

const DIRECTIONS = [
  { id: 'AWA_TO_PREPROD', label: 'AWA Prod \u2192 Azure PreProd', desc: 'On-prem production to Azure preprod (with transforms)', enabled: true },
  { id: 'PREPROD_TO_PROD', label: 'PreProd \u2192 Prod', desc: 'Azure preprod to Azure production', enabled: true },
  { id: 'PROD_TO_PREPROD', label: 'Prod \u2192 PreProd', desc: 'Azure production to Azure preprod', enabled: true }
];

const TRANSFORM_ICONS = {
  rename: Shuffle,
  explicit: Layers,
  merge: Zap,
  filter: Copy
};

const SyncDashboardPage = () => {
  // Setup state
  const [databases, setDatabases] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedDirection, setSelectedDirection] = useState('AWA_TO_PREPROD');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(false);

  // Execute state
  const [dryRun, setDryRun] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const pollingRef = useRef(null);

  // Validation state
  const [expandedTiers, setExpandedTiers] = useState({});
  const [expandedResults, setExpandedResults] = useState(true);

  const meta = selectedProject ? PROJECT_META[selectedProject] : null;
  const syncedCount = meta ? TOTAL_TABLES - meta.excludedTables.length : TOTAL_TABLES;

  // ── Data loading ──────────────────────────────────────────────────
  useEffect(() => {
    databasesApi.getAvailable()
      .then(res => setDatabases(res.data?.filter(d => d.connStringName) || []))
      .catch(() => setDatabases([]));
  }, []);

  const loadHistory = useCallback(async (project) => {
    if (!project) return;
    setHistoryLoading(true);
    try {
      const res = await syncApi.getHistory(project);
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    }
    setHistoryLoading(false);
  }, []);

  // Load history when project changes
  useEffect(() => {
    if (selectedProject) {
      loadHistory(selectedProject);
    }
  }, [selectedProject, loadHistory]);

  // ── Polling ───────────────────────────────────────────────────────
  // Use refs to avoid stale closures in the polling interval
  const selectedProjectRef = useRef(selectedProject);
  selectedProjectRef.current = selectedProject;

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(async () => {
    try {
      const res = await syncApi.getStatus();
      setSyncStatus(res.data);
      if (res.data?.status === 'SUCCESS' || res.data?.status === 'FAILED') {
        stopPolling();
        const proj = selectedProjectRef.current;
        if (proj) loadHistory(proj);
      }
    } catch { /* ignore */ }
  }, [loadHistory, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(pollStatus, 1500);
  }, [pollStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Check if there's already a running sync on mount
  useEffect(() => {
    pollStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Actions ───────────────────────────────────────────────────────
  const startSync = async () => {
    if (!selectedProject || !selectedDirection) return;
    const request = {
      Project: selectedProject,
      Phase: selectedDirection,
      DryRun: dryRun
    };
    try {
      const res = await syncApi.start(request);
      setSyncStatus(res.data);
      // Start polling immediately + on interval
      startPolling();
      // Also do an immediate poll after a short delay (catches fast dry runs)
      setTimeout(pollStatus, 500);
    } catch (err) {
      const msg = err.response?.data?.errorMessage || err.response?.data?.ErrorMessage || err.message;
      setSyncStatus({ status: 'FAILED', errorMessage: msg, project: selectedProject });
    }
  };

  const acknowledge = () => {
    setSyncStatus(null);
    setExpandedResults(true);
    setExpandedTiers({});
    setDryRun(false);
  };

  // ── Helpers ───────────────────────────────────────────────────────
  const isRunning = syncStatus?.status === 'RUNNING';
  const isComplete = syncStatus?.status === 'SUCCESS' || syncStatus?.status === 'FAILED';
  const isCompleteForThisProject = isComplete && syncStatus?.project === selectedProject;
  const hasPendingAck = isComplete && syncStatus?.project && syncStatus.project !== selectedProject;

  const showExecuteTile = selectedProject && selectedDirection;
  const showValidationTile = isCompleteForThisProject;

  // Can't start a new sync while another is running
  const canStartSync = !isRunning;

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '--';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getTransformType = (tableName) => {
    if (!meta) return null;
    return meta.customTransforms.find(t => t.table === tableName);
  };

  const isTableExcluded = (tableName) => {
    if (!meta) return false;
    return meta.excludedTables.includes(tableName);
  };

  // Find tier for a table name
  const getTierForTable = (tableName) => {
    for (const tier of TIERS) {
      if (tier.tables.includes(tableName)) return tier;
    }
    return null;
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="dev-module sync-dashboard-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Database size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Database Sync
          </h1>
          {meta && (
            <span className="dev-module-meta">
              {syncedCount} of {TOTAL_TABLES} tables
              {meta.excludedTables.length > 0 && ` (${meta.excludedTables.length} N/A)`}
            </span>
          )}
        </div>
      </div>

      <div className="dev-module-content">
        <div className="sync-steps-layout">
          {/* ═══════════ TILE 1: SETUP ═══════════ */}
          <div className="stui-step-panel sync-setup-panel">
            <div className="stui-step-header">
              <span className="stui-step-number">1</span>
              <h3>Setup</h3>
            </div>
            <p className="stui-step-desc">Select database and sync direction.</p>

            {/* Database selector */}
            <select
              className="form-input"
              value={selectedProject || ''}
              disabled={isRunning}
              onChange={e => {
                setSelectedProject(e.target.value || null);
                setExpandedTiers({});
              }}
            >
              <option value="">— Select database —</option>
              {databases.map(db => {
                const dbMeta = PROJECT_META[db.connStringName];
                const synced = dbMeta ? TOTAL_TABLES - dbMeta.excludedTables.length : TOTAL_TABLES;
                return (
                  <option key={db.connStringName} value={db.connStringName}>
                    {db.displayName} ({synced}/{TOTAL_TABLES})
                  </option>
                );
              })}
            </select>
            {databases.length === 0 && (
              <span className="sync-no-history">Loading databases...</span>
            )}

            {/* Direction selector */}
            {selectedProject && (
              <div className="sync-direction-list">
                {DIRECTIONS.map(dir => (
                  <label
                    key={dir.id}
                    className={`sync-direction-item ${!dir.enabled ? 'disabled' : ''} ${selectedDirection === dir.id ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="direction"
                      value={dir.id}
                      checked={selectedDirection === dir.id}
                      onChange={() => setSelectedDirection(dir.id)}
                      disabled={!dir.enabled || isRunning}
                    />
                    <div className="sync-direction-info">
                      <span className="sync-direction-label">
                        {dir.label}
                        {!dir.enabled && <Lock size={12} className="sync-lock-icon" />}
                      </span>
                      <span className="sync-direction-desc">{dir.desc}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {/* History */}
            {selectedProject && (
              <div className="sync-history-section">
                <button
                  className="sync-history-toggle"
                  onClick={() => setExpandedHistory(!expandedHistory)}
                >
                  {expandedHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <Clock size={14} />
                  History {historyLoading ? '...' : `(${history.length})`}
                </button>

                {expandedHistory && history.length > 0 && (
                  <div className="sync-history-table-wrap">
                    <table className="sync-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Phase</th>
                          <th className="center">Status</th>
                          <th className="right">Tables</th>
                          <th className="right">Rows</th>
                          <th className="right">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.slice(0, 10).map((h, i) => (
                          <tr key={i}>
                            <td>{formatDate(h.started_At)}</td>
                            <td>{h.sync_Phase}</td>
                            <td className="center">
                              <span className={`sync-status-badge ${h.error_Message === 'DRY RUN' ? 'dryrun' : h.status?.toLowerCase()}`}>
                                {h.error_Message === 'DRY RUN' ? 'DRY RUN' : h.status}
                              </span>
                            </td>
                            <td className="right">{h.tables_Synced || '--'}</td>
                            <td className="right">{h.total_Rows?.toLocaleString() || '--'}</td>
                            <td className="right">{formatDuration(h.duration_Sec)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {expandedHistory && history.length === 0 && !historyLoading && (
                  <p className="sync-no-history">No sync history for {selectedProject}.</p>
                )}
              </div>
            )}
          </div>

          {/* ═══════════ TILE 2: EXECUTE ═══════════ */}
          <div className={`stui-step-panel sync-execute-panel ${isRunning ? 'running' : ''}`}>
          {showExecuteTile && (
            <>
              <div className="stui-step-header">
                <span className="stui-step-number">2</span>
                <h3>Execute</h3>
              </div>

              {/* Pending ack banner for another project */}
              {hasPendingAck && (
                <div className="sync-pending-ack">
                  <AlertTriangle size={14} />
                  <span>
                    <strong>{syncStatus.project}</strong> sync {syncStatus.status === 'SUCCESS' ? 'completed' : 'failed'} —
                    switch back to review, or{' '}
                    <button className="sync-ack-inline" onClick={acknowledge}>dismiss</button>
                  </span>
                </div>
              )}

              {/* Ready to sync (not running, not complete for THIS project) */}
              {!isRunning && !isCompleteForThisProject && (
                <>
                  <p className="stui-step-desc">
                    Sync <strong>{selectedProject}</strong> from{' '}
                    {DIRECTIONS.find(d => d.id === selectedDirection)?.label || selectedDirection}.
                  </p>

                  {/* Tier summary */}
                  <div className="sync-tier-summary">
                    {TIERS.map(t => {
                      const excluded = t.tables.filter(tbl => isTableExcluded(tbl)).length;
                      const active = t.tables.length - excluded;
                      return (
                        <div key={t.tier} className={`sync-tier-row ${active === 0 ? 'all-excluded' : ''}`}>
                          <span className={`sync-tier-badge tier-${t.tier}`}>T{t.tier}</span>
                          <span className="sync-tier-label">{t.label}</span>
                          <span className="sync-tier-count">
                            {active}/{t.tables.length}
                            {excluded > 0 && <span className="sync-tier-na"> ({excluded} N/A)</span>}
                          </span>
                        </div>
                      );
                    })}
                    {meta.customTransforms.length > 0 && selectedDirection === 'AWA_TO_PREPROD' && (
                      <div className="sync-custom-note">
                        {meta.customTransforms.length} table{meta.customTransforms.length !== 1 ? 's' : ''} with custom transforms
                      </div>
                    )}
                  </div>

                  {/* Dry run toggle + sync button */}
                  <div className="sync-action-row">
                    <label className="sync-dryrun-toggle">
                      <input
                        type="checkbox"
                        checked={dryRun}
                        onChange={(e) => setDryRun(e.target.checked)}
                      />
                      Dry run
                    </label>
                    <button className="import-execute-btn" onClick={startSync} disabled={!canStartSync}>
                      <Play size={16} />
                      {dryRun ? 'Preview Sync' : 'Start Sync'}
                    </button>
                  </div>
                </>
              )}

              {/* Running state */}
              {isRunning && (
                <div className="sync-running-status">
                  <div className="sync-running-header">
                    <Loader size={18} className="spinning" />
                    <span>Syncing {syncStatus.project}...</span>
                  </div>

                  {syncStatus.currentStep && (
                    <div className="sync-current-step">{syncStatus.currentStep}</div>
                  )}

                  <div className="sync-running-stats">
                    <span><Clock size={13} /> {formatDuration(syncStatus.elapsedSeconds)}</span>
                    {syncStatus.rowsCopied > 0 && (
                      <span>{syncStatus.rowsCopied.toLocaleString()} rows</span>
                    )}
                  </div>

                  {syncStatus.tablesTotal > 0 && (
                    <div className="import-progress">
                      <div className="import-progress-bar">
                        <div
                          className="import-progress-fill"
                          style={{ width: `${Math.round((syncStatus.tablesCompleted / syncStatus.tablesTotal) * 100)}%` }}
                        />
                      </div>
                      <span className="import-progress-text">
                        {syncStatus.tablesCompleted}/{syncStatus.tablesTotal} tables ({Math.round((syncStatus.tablesCompleted / syncStatus.tablesTotal) * 100)}%)
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Complete state (brief — validation tile has the details) */}
              {isCompleteForThisProject && (
                <div className={`sync-complete-brief ${syncStatus.status.toLowerCase()}`}>
                  {syncStatus.status === 'SUCCESS' ? (
                    <CheckCircle size={18} className="icon-success" />
                  ) : (
                    <XCircle size={18} className="icon-fail" />
                  )}
                  <div>
                    <div className="sync-complete-label">{syncStatus.status === 'SUCCESS' ? 'Sync Complete' : 'Sync Failed'}</div>
                    <div className="sync-complete-stats">
                      {formatDuration(syncStatus.elapsedSeconds)}
                      {syncStatus.rowsCopied > 0 && ` \u2022 ${syncStatus.rowsCopied.toLocaleString()} rows`}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          </div>

          {/* ═══════════ TILE 3: VALIDATION ═══════════ */}
          <div className="stui-step-panel sync-validation-panel">
          {showValidationTile && (
            <>
              <div className="stui-step-header">
                <span className="stui-step-number">3</span>
                <h3>Validation</h3>
              </div>

              {/* Overall result */}
              <div className={`sync-val-result ${syncStatus.status.toLowerCase()}`}>
                <div className="sync-val-summary">
                  {syncStatus.status === 'SUCCESS' ? (
                    <>
                      <CheckCircle size={20} />
                      <span>
                        {syncStatus.currentStep?.includes('Dry run')
                          ? `Dry run complete — ${syncStatus.tableResults?.length || syncedCount} tables verified`
                          : `${syncStatus.tableResults?.length || syncedCount} tables synced successfully`}
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle size={20} />
                      <span>Sync failed</span>
                    </>
                  )}
                </div>

                {syncStatus.errorMessage && (
                  <div className="sync-val-error">
                    <AlertTriangle size={14} />
                    <span>{syncStatus.errorMessage}</span>
                  </div>
                )}
              </div>

              {/* No source data callout */}
              {syncStatus.status === 'SUCCESS' && meta?.excludedTables.length > 0 && (
                <div className="sync-val-no-source">
                  <div className="sync-val-no-source-header">
                    <Ban size={14} />
                    <span>No Source Data ({meta.excludedTables.length} tables)</span>
                  </div>
                  <div className="sync-val-no-source-list">
                    {meta.excludedTables.map(t => (
                      <div key={t} className="sync-val-no-source-item">
                        <span className="sync-val-no-source-name">{t}</span>
                        <span className="sync-val-no-source-reason">not in {selectedProject} source</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tier-by-tier breakdown */}
              {syncStatus.status === 'SUCCESS' && meta && (
                <div className="sync-val-tiers">
                  {TIERS.map(tier => {
                    const isExpanded = expandedTiers[tier.tier];
                    const tierResults = (syncStatus.tableResults || []).filter(r =>
                      tier.tables.includes(r.tableName)
                    );
                    const excludedInTier = tier.tables.filter(t => isTableExcluded(t));
                    const activeInTier = tier.tables.length - excludedInTier.length;

                    return (
                      <div key={tier.tier} className="sync-val-tier-group">
                        <div
                          className="sync-val-tier-header"
                          onClick={() => setExpandedTiers(prev => ({ ...prev, [tier.tier]: !prev[tier.tier] }))}
                        >
                          <span className={`sync-tier-badge tier-${tier.tier}`}>T{tier.tier}</span>
                          <span className="sync-val-tier-label">{tier.label}</span>
                          <span className="sync-val-tier-count">
                            {tierResults.length}/{activeInTier}
                            {excludedInTier.length > 0 && ` (+${excludedInTier.length} N/A)`}
                          </span>
                          <span className="sync-val-tier-status">
                            {tierResults.length > 0 && tierResults.every(r => r.match !== false) ? (
                              <CheckCircle size={14} className="icon-success" />
                            ) : tierResults.length === 0 && activeInTier === 0 ? (
                              <Ban size={14} className="icon-skip" />
                            ) : (
                              <AlertTriangle size={14} className="icon-warning" />
                            )}
                          </span>
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </div>

                        {isExpanded && (
                          <div className="sync-val-tier-tables">
                            {tier.tables.map(tableName => {
                              const excluded = isTableExcluded(tableName);
                              const result = tierResults.find(r => r.tableName === tableName);
                              const transform = getTransformType(tableName);

                              return (
                                <div key={tableName} className={`sync-val-table-row ${excluded ? 'excluded' : ''}`}>
                                  <span className="sync-val-table-icon">
                                    {excluded ? (
                                      <Ban size={14} className="icon-na" />
                                    ) : result ? (
                                      result.match !== false ? <CheckCircle size={14} className="icon-success" /> : <XCircle size={14} className="icon-fail" />
                                    ) : (
                                      <span className="icon-skip">--</span>
                                    )}
                                  </span>
                                  <span className="sync-val-table-name">{tableName}</span>
                                  {excluded ? (
                                    <span className="sync-transform-badge na">N/A</span>
                                  ) : transform && selectedDirection === 'AWA_TO_PREPROD' ? (
                                    <span className={`sync-transform-badge ${transform.type}`} title={transform.desc}>
                                      {React.createElement(TRANSFORM_ICONS[transform.type] || Zap, { size: 10 })}
                                      {transform.type}
                                    </span>
                                  ) : (
                                    <span className="sync-transform-badge straight" title="Direct column-matched copy">
                                      <ArrowRight size={10} />
                                      copy
                                    </span>
                                  )}
                                  <span className="sync-val-table-rows">
                                    {excluded ? '' : result?.destRows != null ? result.destRows.toLocaleString() + ' rows' : '--'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Expandable full table results */}
              {syncStatus.status === 'SUCCESS' && syncStatus.tableResults?.length > 0 && (
                <div className="sync-val-full-results">
                  <button
                    className="sync-history-toggle"
                    onClick={() => setExpandedResults(!expandedResults)}
                  >
                    {expandedResults ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    All table results ({syncStatus.tableResults.length})
                  </button>

                  {expandedResults && (
                    <div className="sync-history-table-wrap">
                      <table className="sync-history-table">
                        <thead>
                          <tr>
                            <th>Table</th>
                            <th>Type</th>
                            <th className="right">Rows</th>
                            <th className="center">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {syncStatus.tableResults.map((t, i) => {
                            const transform = getTransformType(t.tableName);
                            const tier = getTierForTable(t.tableName);
                            return (
                              <tr key={i}>
                                <td>
                                  {tier && <span className={`sync-tier-badge-sm tier-${tier.tier}`}>T{tier.tier}</span>}
                                  {t.tableName}
                                </td>
                                <td>
                                  {transform && selectedDirection === 'AWA_TO_PREPROD' ? (
                                    <span className={`sync-transform-badge ${transform.type}`}>
                                      {transform.type}
                                    </span>
                                  ) : (
                                    <span className="sync-transform-badge straight">copy</span>
                                  )}
                                </td>
                                <td className="right">{t.destRows?.toLocaleString() || '--'}</td>
                                <td className="center">
                                  {t.match !== false ? (
                                    <CheckCircle size={14} className="icon-success" />
                                  ) : (
                                    <XCircle size={14} className="icon-fail" />
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Custom transforms legend — AWA_TO_PREPROD only */}
              {syncStatus.status === 'SUCCESS' && meta?.customTransforms.length > 0 && selectedDirection === 'AWA_TO_PREPROD' && (
                <div className="sync-val-transforms">
                  <div className="sync-val-section-label">Custom Transforms</div>
                  {meta.customTransforms.map((t, i) => (
                    <div key={i} className="sync-val-transform-row">
                      <span className={`sync-transform-badge ${t.type}`}>
                        {React.createElement(TRANSFORM_ICONS[t.type] || Zap, { size: 10 })}
                        {t.type}
                      </span>
                      <span className="sync-val-transform-table">{t.table}</span>
                      <span className="sync-val-transform-desc">{t.desc}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Acknowledge button */}
              <button className="sync-acknowledge-btn" onClick={acknowledge}>
                <RotateCcw size={16} />
                Acknowledge &amp; Reset
              </button>
            </>
          )}
          </div>
        </div>
      </div>

      <style>{`
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default SyncDashboardPage;
